import { Box, Button, Link } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { useFormik } from 'formik';
import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { offerSelected } from '../../../../app/reducers/offer-selected';
import { Offer } from '../../../../domain/qualification';
import { RequiredText } from '../../../../general/components/required-text';
import { FormikToggle } from '../../../../general/inputs/formik-toggle';
import { recordOfferConsent, selectConsent } from '../../../legal-consent/reducers/consent';
import { selectOffer } from '../../reducers/credit-terms.reducer';
import { nextStep, selectnextStep } from '../../reducers/next-step';

const savingSelector = createSelector(
  [selectConsent, selectnextStep],
  (consent, { loading }) => consent.loading || loading,
);

const validationSchema = yup.object({
  consentOfferSummary: yup.mixed().oneOf([true], 'You must read and accept the Coast Offer Summary'),
});

type Props = {
  offers: Offer[];
  selectedOfferId: string | null;
};

const MultipleOfferConsentForm: FC<Props> = ({ offers, selectedOfferId }) => {
  const dispatch = useDispatch();
  const saving = useSelector(savingSelector);
  const selectedOffer = offers.find((o) => o.id === selectedOfferId);

  const formik = useFormik({
    initialValues: { consentOfferSummary: false },
    validationSchema,
    validateOnChange: false,
    onSubmit: () => {
      if (!selectedOffer) return;
      dispatch(
        recordOfferConsent({
          offerId: selectedOffer.id,
          onSuccess: [
            offerSelected({ offer: selectedOffer.offerIndex }),
            selectOffer(selectedOffer.id),
            nextStep({ action: 'select_offer', gotoNextPage: true }),
          ],
        }),
      );
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box sx={{ width: 'fit-content', maxWidth: '100%', mx: 'auto' }}>
        <FormikToggle
          formik={formik}
          id="consentOfferSummary"
          name="consentOfferSummary"
          disabled={!selectedOffer}
          label={
            <RequiredText>
              I have reviewed and acknowledge receipt of the{' '}
              {selectedOffer ? (
                <Link href={selectedOffer.offerSummaryUrl} target="_blank">
                  Coast Offer Summary
                </Link>
              ) : (
                'Coast Offer Summary'
              )}
            </RequiredText>
          }
          toggleStyle="checkbox"
        />
      </Box>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          borderTop: 1,
          borderColor: 'divider',
          py: 2,
          px: 2,
        }}
      >
        <Button
          variant="contained"
          color="coolTonedGradient"
          size="large"
          fullWidth
          type="submit"
          disabled={!selectedOfferId || !formik.values.consentOfferSummary || saving}
          sx={{ maxWidth: 375 }}
        >
          Continue with plan
        </Button>
      </Box>
    </form>
  );
};

export { MultipleOfferConsentForm };
